import { Component, Input } from '@angular/core';

/**
 * Loading placeholders for the two card shapes — iOS `EventCardShimmerView` and
 * `EventVerticalCardShimmerView`.
 *
 * One component with a `variant` rather than two, because the two skeletons differ only in
 * proportions: the shapes inside are the same title / facts / media blocks. `bar` is the shared
 * pulsing rectangle.
 *
 * Marked `aria-hidden` — a screen reader should hear the list's `aria-busy`, not a tree of
 * meaningless boxes.
 */
@Component({
    selector: 'app-event-skeleton',
    host: { class: 'events-scope', 'aria-hidden': 'true' },
    template: `
    <div class="ev-sk" [class.ev-sk-featured]="variant === 'featured'">
      <div class="ev-sk-media"></div>

      <div class="ev-sk-body">
        <div class="ev-sk-bar" style="width: 70%; height: 20px;"></div>
        <div class="ev-sk-bar" style="width: 45%; height: 20px;"></div>

        <div class="ev-sk-facts">
          <div class="ev-sk-bar" style="width: 100px; height: 16px;"></div>
          <div class="ev-sk-bar" style="width: 70px; height: 16px;"></div>
          <div class="ev-sk-bar ev-sk-round" style="width: 32px; height: 32px;"></div>
        </div>
      </div>
    </div>
  `,
    styles: [`
    :host { display: block; height: 100%; }

    .ev-sk {
      display: flex;
      flex-direction: column;
      height: 100%;
      border-radius: 20px;
      overflow: hidden;
      background: var(--ev-surface, #232323);
    }

    .ev-sk-media {
      height: 200px;
      flex: 0 0 auto;
      background: var(--ev-shimmer-tint, rgba(255, 255, 255, 0.08));
      animation: evSkeletonPulse 1.4s ease-in-out infinite;
    }
    /* The featured card is image-dominant and sized by its carousel, so its media block takes
       whatever height is left rather than a fixed 200px. */
    .ev-sk-featured .ev-sk-media {
      flex: 1 1 auto;
      height: auto;
    }

    .ev-sk-body {
      display: flex;
      flex-direction: column;
      gap: 8px;
      padding: 20px 12px 12px;
    }

    .ev-sk-facts {
      display: flex;
      align-items: center;
      gap: 10px;
      margin-top: 4px;
    }
    .ev-sk-facts .ev-sk-round { margin-left: auto; }

    .ev-sk-bar {
      border-radius: 6px;
      background: var(--ev-shimmer-tint, rgba(255, 255, 255, 0.08));
      animation: evSkeletonPulse 1.4s ease-in-out infinite;
    }
    .ev-sk-round { border-radius: 50%; }

    /* Opacity, not background-position: no layout or paint of a gradient the size of the card, and
       it matches the app's existing mediaShimmer idiom. */
    @keyframes evSkeletonPulse {
      0%, 100% { opacity: 0.55; }
      50%      { opacity: 1; }
    }
  `],
    standalone: false
})
export class EventSkeletonComponent {
  @Input() variant: 'featured' | 'list' = 'list';
}
